import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { Store } from '@ngrx/store';
import { takeUntil, take, withLatestFrom, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  Template,
  ChannelTypeIcons,
  ConfiguredChannel,
  ChannelType,
  ChannelUIds,
  EverbridgeTemplate
} from 'src/app/modules/management/store/communication-center';
import { UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import * as fromSharing from 'src/app/modules/sharing/store';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import * as fromImpactedAssets from 'src/app/modules/cop/store/impacted-assets';
import { QuillEditorService } from 'src/app/modules/core/services/quill/quill.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'topo-share-document',
  templateUrl: './share-document.component.html',
  styleUrls: ['./share-document.component.scss'],
  standalone: false
})
export class ShareDocumentComponent implements OnInit, OnDestroy {
  @Input() set visible(value: boolean) {
    this._visible = value;
    this.visible$.next(value);
  }
  _visible: boolean;
  @Input() dataset: Dataset;
  @Input() document: Document;
  @Input() mapUId: string;
  @Input() distanceDropdownValue: any;
  @Input() communicationMeta: any;
  @Input() isClosestAssetWorking: boolean;
  @Input() selectedDocumentAssetsByDistance: any[];
  @Input() doEmailsExist: boolean;
  @Input() emailClosestAssetData: any[];
  @Input() pathToMap: string = 'map';

  @Output() done: EventEmitter<void> = new EventEmitter();
  @Output() loadClosestAssets: EventEmitter<string> = new EventEmitter();

  destroy$ = new Subject<void>();
  visible$ = new Subject<boolean>();
  channels: ConfiguredChannel[] = [];
  templates: Template[] = [];
  everbridgeTemplates: EverbridgeTemplate[] = [];
  selectedTemplate: Template;
  selectedChannel: ConfiguredChannel;
  isShareActive: boolean = false;
  showTemplates: boolean = false;
  showTextInput: boolean = false;
  editor: any;
  message: string;
  organizationUId: string;
  isPreviewButton: boolean = true;
  isEverbridgeShareActive = false;

  constructor(
    private store: Store<any>,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    private userProfileApiService: UserProfileApiService,
    private editorService: QuillEditorService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromAuth.selectUserContext)
      .pipe(take(1))
      .subscribe(uc => (this.organizationUId = uc.organizationUId));

    this.store
      .select(fromSharing.selectConfiguredChannels)
      .pipe(takeUntil(this.destroy$))
      .subscribe(channels => {
        if (!channels) {
          return;
        }

        this.selectedChannel = null;
        this.channels = channels
          .filter(c => c.isActive === true)
          .map(c => {
            var channelIcon = ChannelTypeIcons[c.channelType];
            if (c.channelUId == ChannelUIds.SlackChannelUId) {
              channelIcon = 'icon-logo_slack';
            }
            if (c.channelUId == ChannelUIds.MicrosoftTeamsChannelUId) {
              channelIcon = 'icon-logo_ms_teams';
            }

            return {
              ...c,
              channelTypeDisplay: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${c.channelType}`),
              channelIcon: channelIcon
            };
          });
      });

    this.store
      .select(fromSharing.selectCommunicationTemplates)
      .pipe(takeUntil(this.destroy$))
      .subscribe(templates => {
        if (!templates) {
          return;
        }

        this.selectedTemplate = null;
        this.templates = templates
          .filter(t => t.isActive === true)
          .map(c => {
            return {
              ...c,
              channelTypeDisplay: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${c.channelType}`),
              channelIcon: 'icon-template'
            };
          });
      });

    this.store
      .select(fromSharing.selectEverbridgeTemplates)
      .pipe(takeUntil(this.destroy$))
      .subscribe(templates => {
        if (!templates) {
          return;
        }

        this.selectedTemplate = null;
        this.everbridgeTemplates = templates;
        this.templates = templates.map(c => {
          return {
            ...this.everbridgeTemplateToTemplateDropdown(c),
            channelIcon: 'icon-template'
          };
        });
      });

    this.visible$.pipe(takeUntil(this.destroy$)).subscribe(visible => {
      if (visible) {
        this.getConfiguredChannels();
      } else {
        this.templates = null;
        this.channels = null;
        this.everbridgeTemplates = null;
        this.selectedTemplate = null;
        this.selectedChannel = null;
        this.isPreviewButton = false;
        this.showTemplates = false;
        this.showTextInput = false;
      }
    });

    this.store
      .select(fromSharing.selectShowShareDocumentDialog)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => {
        this.isShareActive = isActive;
      });

    this.store
      .select(fromSharing.selectShowShareDocumentEverbridgeDialog)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => {
        this.isEverbridgeShareActive = isActive;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get editorModules() {
    this.editorService.init();
    return {
      toolbar: {
        container: [['bold', 'italic', 'link', { redo: 'redo' }, { undo: 'undo' }]],
        handlers: {
          redo: () => {
            this.editor?.history.redo();
          },
          undo: () => {
            this.editor?.history.undo();
          }
        }
      },
      history: { delay: 2000, maxStack: 500, userOnly: true },
      mention: {
        minChars: 1,
        mentionDenotationChars: ['@'],
        source: (searchTerm, renderList) => {
          this.getUserList(searchTerm, renderList);
        }
      }
    };
  }

  editorCreated(editor: any) {
    this.editor = editor;
    this.editor.focus();
  }

  animationStarted(event$): void {}

  animationDone(event$): void {}

  getConfiguredChannels(): void {
    const queryParams = {
      skip: 0,
      take: 100,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };

    this.store.dispatch(fromSharing.getConfiguredChannelsFromApi({ queryParams }));
  }

  getCommunicationTemplates(): void {
    let query = `ChannelType=ChannelType.${ChannelType[this.selectedChannel?.channelType]} && DatasetUId="${this.dataset.uId}"`;

    if (this.selectedChannel?.channelType === ChannelType.App && this.selectedChannel?.channelUId === ChannelUIds.SlackChannelUId) {
      query += ` && ChannelUId="${this.selectedChannel.channelUId}"`;
    }

    if (
      this.selectedChannel?.channelType === ChannelType.App &&
      this.selectedChannel?.channelUId === ChannelUIds.MicrosoftTeamsChannelUId
    ) {
      query += ` && ChannelUId="${this.selectedChannel.channelUId}"`;
    }

    const queryParams = {
      skip: 0,
      take: 100,
      sortField: 'name',
      sortDirection: 'Ascending',
      where: query
    };

    this.store.dispatch(fromSharing.getCommunicationTemplatesFromApi({ queryParams }));
  }

  getEverbridgeTemplates(): void {
    this.store.dispatch(fromSharing.getEverbridgeTemplatesForConfiguredChannelFromApi({ channel: this.selectedChannel }));
  }

  cancel(): void {
    this.done.emit();
  }

  selectChannel(channel: ConfiguredChannel): void {
    this.selectedChannel = channel;

    this.isPreviewButton = false;
    this.showTemplates = false;
    this.showTextInput = false;

    this.selectedTemplate = null;
    this.templates = null;
    this.everbridgeTemplates = null;

    if (this.selectedChannel?.channelUId === ChannelUIds.SlackChannelUId) {
      return this.showSlack();
    } else if (this.selectedChannel?.channelUId === ChannelUIds.EverbridgeChannelUId) {
      return this.showEverbridge();
    } else if (this.selectedChannel?.channelType === ChannelType.Email) {
      return this.showEmail();
    } else if (this.selectedChannel?.channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
      return this.showMicrosoftTeams();
    } else if (this.selectedChannel?.channelType === ChannelType.Sms) {
      return this.showEmail();
    }
  }

  resetForm() {
    this.isPreviewButton = false;
    this.showTemplates = false;
    this.showTextInput = false;

    this.selectedTemplate = null;
    this.templates = [];
    this.everbridgeTemplates = [];
  }

  showSlack() {
    this.isPreviewButton = true;
    this.showTemplates = true;

    this.getCommunicationTemplates();
  }

  showEmail() {
    this.isPreviewButton = true;
    this.showTemplates = true;

    this.getCommunicationTemplates();
  }

  showEverbridge() {
    this.isPreviewButton = true;
    this.showTemplates = true;

    this.getEverbridgeTemplates();
  }

  showMicrosoftTeams() {
    this.isPreviewButton = true;
    this.showTemplates = true;

    this.getCommunicationTemplates();
  }

  selectTemplate(template: Template): void {
    this.selectedTemplate = template;
  }

  send(): void {
    if (!this.selectedChannel || !this.selectedChannel?.channelUId || !this.selectedChannel?.channelType) {
      this.translate.get('MAP.ERROR-CHANNEL-REQUIRED').subscribe((res: string) => {
        this.notify.error(res);
      });

      return;
    }

    if (this.showTemplates && !this.selectedTemplate) {
      this.translate.get('MAP.ERROR-TEMPLATE-REQUIRED').subscribe((res: string) => {
        this.notify.error(res);
      });

      return;
    }

    if (this.selectedChannel.channelUId === ChannelUIds.SlackChannelUId) {
      this.requestClosesetAssets(this.selectedChannel?.settings?.sendDirectMessages === true, true);

      this.store.dispatch(
        fromSharing.showShareDocumentDialog({
          selectedChannel: this.selectedChannel,
          selectedTemplate: this.selectedTemplate
        })
      );

      return;
    }

    if (this.selectedChannel.channelUId === ChannelUIds.MicrosoftTeamsChannelUId || this.selectedChannel.channelType === ChannelType.Sms) {
      if (this.selectedChannel.channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
        this.requestClosesetAssets(this.selectedChannel?.settings?.sendDirectMessages === true, true);
      }

      this.store.dispatch(
        fromSharing.showShareDocumentDialog({
          selectedChannel: this.selectedChannel,
          selectedTemplate: this.selectedTemplate
        })
      );

      return;
    }

    if (this.selectedChannel.channelType === ChannelType.Email) {
      this.requestClosesetAssets(true, true);

      this.store.dispatch(
        fromSharing.showShareDocumentDialog({
          selectedChannel: this.selectedChannel,
          selectedTemplate: this.selectedTemplate
        })
      );

      return;
    }

    if (this.selectedChannel.channelUId === ChannelUIds.EverbridgeChannelUId) {
      var everbridgeTemplate = this.everbridgeTemplates.find(t => t.id.toString() === this.selectedTemplate?.uId);

      if (!everbridgeTemplate) {
        this.translate.get('MAP.ERROR-TEMPLATE-REQUIRED').subscribe((res: string) => {
          this.notify.error(res);
        });

        return;
      }

      // Gets closest assets so that the dialog can decide if there are everbridge contacts nearby
      if (this.dataset?.type === DatasetType.Alerts) {
        this.store.dispatch(fromImpactedAssets.setClosestIsWorking({ isWorking: true }));
        this.loadClosestAssets.emit('asset');
      }

      this.store.dispatch(
        fromSharing.showShareDocumentEverbridgeDialog({
          selectedChannel: this.selectedChannel,
          selectedEverbridgeTemplate: everbridgeTemplate
        })
      );

      return;
    }
  }

  private requestClosesetAssets(emails: boolean, assets: boolean): void {
    if (emails === true) {
      // Gets closest assets with emails for the recipients list
      if (this.dataset?.type === DatasetType.Alerts && this.doEmailsExist) {
        this.loadClosestAssets.emit('email');
      }
    }
    if (assets === true) {
      // Gets all closest assets for the communication closest assets table
      if (this.dataset?.type === DatasetType.Alerts && this.selectedTemplate?.layout?.includes('topo-selected-assets')) {
        this.loadClosestAssets.emit('asset');
      }
    }
  }

  getUserList(searchText, renderList) {
    this.userProfileApiService.getUsersForMention(this.organizationUId, searchText).subscribe(res => {
      const entries = res.data.map(p => ({ id: p.uId, value: `${p.displayName}` }));
      renderList(entries);
    });
  }

  everbridgeTemplateToTemplateDropdown(e: EverbridgeTemplate): Template {
    return {
      uId: e.id.toString(),
      name: e.title,
      description: e.title,
      channelType: ChannelType.MassNotifications,
      channelTypeDisplay: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${ChannelType.MassNotifications}`),
      datasetUId: null,
      layout: null,
      isActive: true,
      createdOn: null,
      updatedOn: null,
      lastEditedByName: null,
      lastEditedById: null,
      settings: {
        toRecipients: [],
        ccRecipients: [],
        bccRecipients: [],
        toReadOnly: false,
        ccReadOnly: false,
        subject: null,
        sendAsGroup: false,
        sendDocumentAttachments: false
      }
    };
  }
}
